import React, { Component } from 'react';
import { createPortal } from 'react-dom';
import { ImageLoader } from './ImageLoader';
import { MEDIA_VARIANTS } from '../../media';
import './Game_ItemTooltip.css';
import GameAPI from './GameAPI';
import { GameItemStats } from './Game_ItemStats';
import {
    buildConsumableEffectTargets,
    formatConsumableDuration,
    formatConsumableEffect,
    getConsumableDurationSeconds,
    getConsumableEffectType,
    getConsumableEffects,
    getConsumableTargetValue,
    isTimedConsumableEffect,
} from './consumableEffects';

let tooltipInstanceCount = 0;

export class GameItemTooltip extends Component {
    constructor(props) {
        super(props);
        this.state = {
            itemId: "",
            itemInstanceId: "",
            item: null,
            rarity: null,
            itemType: null,
            comparisonItemId: "",
            comparisonItemInstanceId: "",
            comparisonItem: null,
            comparisonRarity: null,
            comparisonItemType: null,
            consumableEffectTargets: [],
            expandedDescriptions: {
                primary: false,
                comparison: false,
            },
            overflowingDescriptions: {
                primary: false,
                comparison: false,
            },
            position: {
                top: 0,
                left: 0,
            },
            positioned: false,
        };
        this.tooltipRef = React.createRef();
        this.tooltipInstanceId = `game-item-tooltip-${tooltipInstanceCount += 1}`;
        this.descriptionRefs = {
            primary: null,
            comparison: null,
        };
        this.descriptionRefCallbacks = {
            primary: (node) => this.setDescriptionRef('primary', node),
            comparison: (node) => this.setDescriptionRef('comparison', node),
        };
        this.anchorElement = null;
        this.isMountedFlag = false;
    }

    async componentDidMount() {
        this.isMountedFlag = true;
        this.anchorElement = this.props.getAnchorElement?.() ?? null;
        window.addEventListener('resize', this.handleViewportChange);
        window.addEventListener('scroll', this.handleViewportChange, true);
        document.addEventListener('dragstart', this.handleDocumentDragStart);
        document.addEventListener('pointerdown', this.handleDocumentPointerDown);
        document.addEventListener('keydown', this.handleDocumentKeyDown);
        await Promise.all([
            this.loadConsumableEffectTargets(),
            this.loadItem(this.props.itemId, this.props.itemInstanceId),
            this.loadComparisonItem(
                this.props.comparisonItemId,
                this.props.comparisonItemInstanceId,
            ),
        ]);
    }

    loadConsumableEffectTargets = async () => {
        const catalog = await GameAPI.fetchConsumableEffectTargets();
        if (!this.isMountedFlag) {
            return;
        }
        this.setState(
            { consumableEffectTargets: buildConsumableEffectTargets(catalog) },
            this.updatePosition,
        );
    };

    async componentDidUpdate(prevProps) {
        if (
            (this.props.interactive && !prevProps.interactive)
            || this.props.itemInstanceId !== prevProps.itemInstanceId
            || this.props.itemId !== prevProps.itemId
        ) {
            this.anchorElement = this.props.getAnchorElement?.() ?? this.anchorElement;
        }

        const itemChanged = this.props.itemInstanceId !== prevProps.itemInstanceId
            || this.props.itemId !== prevProps.itemId;
        const comparisonChanged = this.props.comparisonItemInstanceId !== prevProps.comparisonItemInstanceId
            || this.props.comparisonItemId !== prevProps.comparisonItemId;

        if (itemChanged || comparisonChanged) {
            await Promise.all([
                itemChanged
                    ? this.loadItem(this.props.itemId, this.props.itemInstanceId)
                    : Promise.resolve(),
                comparisonChanged
                    ? this.loadComparisonItem(
                        this.props.comparisonItemId,
                        this.props.comparisonItemInstanceId,
                    )
                    : Promise.resolve(),
            ]);
        } else if (this.props.anchorRect !== prevProps.anchorRect || this.props.getAnchorRect !== prevProps.getAnchorRect) {
            this.updatePosition();
        }

        if (this.props.interactive && !prevProps.interactive) {
            this.refreshTooltipLayout(true);
        }
    }

    componentWillUnmount() {
        this.isMountedFlag = false;
        window.removeEventListener('resize', this.handleViewportChange);
        window.removeEventListener('scroll', this.handleViewportChange, true);
        document.removeEventListener('dragstart', this.handleDocumentDragStart);
        document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
        document.removeEventListener('keydown', this.handleDocumentKeyDown);
        if (this.tooltipRef.current?.contains(document.activeElement)) {
            this.anchorElement?.focus({ preventScroll: true });
        }
    }

    loadItem = async (itemId, itemInstanceId) => {
        if (!itemId && !itemInstanceId) {
            this.setState({ item: null, rarity: null, itemType: null, positioned: false });
            return;
        }

        if (this.state.itemId === itemId && this.state.itemInstanceId === itemInstanceId && this.state.item) {
            this.updatePosition();
            return;
        }

        const item = await GameAPI.fetchItemAuto(itemId, itemInstanceId);
        if (
            !this.isMountedFlag
            || this.props.itemId !== itemId
            || this.props.itemInstanceId !== itemInstanceId
        ) {
            return;
        }

        const metadata = Promise.all([
            GameAPI.fetchRarity(item?.rarityId),
            GameAPI.fetchItemType(item?.typeId),
        ]);
        this.setState((previousState) => ({
            itemId,
            itemInstanceId,
            item,
            rarity: null,
            itemType: null,
            positioned: false,
            expandedDescriptions: {
                ...previousState.expandedDescriptions,
                primary: false,
            },
            overflowingDescriptions: {
                ...previousState.overflowingDescriptions,
                primary: false,
            },
        }), () => this.refreshTooltipLayout(this.props.interactive));

        const [rarity, itemType] = await metadata;
        if (
            !this.isMountedFlag
            || this.props.itemId !== itemId
            || this.props.itemInstanceId !== itemInstanceId
        ) {
            return;
        }
        this.setState({ rarity, itemType }, this.refreshTooltipLayout);
    };

    loadComparisonItem = async (itemId, itemInstanceId) => {
        if (!itemId && !itemInstanceId) {
            if (this.state.comparisonItem) {
                this.setState({
                    comparisonItemId: "",
                    comparisonItemInstanceId: "",
                    comparisonItem: null,
                    comparisonRarity: null,
                    comparisonItemType: null,
                }, this.updatePosition);
            }
            return;
        }

        if (
            this.state.comparisonItemId === itemId
            && this.state.comparisonItemInstanceId === itemInstanceId
            && this.state.comparisonItem
        ) {
            this.updatePosition();
            return;
        }

        const comparisonItem = await GameAPI.fetchItemAuto(itemId, itemInstanceId);
        const [comparisonRarity, comparisonItemType] = await Promise.all([
            GameAPI.fetchRarity(comparisonItem?.rarityId),
            GameAPI.fetchItemType(comparisonItem?.typeId),
        ]);
        if (!this.isMountedFlag) {
            return;
        }

        this.setState((previousState) => ({
            comparisonItemId: itemId,
            comparisonItemInstanceId: itemInstanceId,
            comparisonItem,
            comparisonRarity,
            comparisonItemType,
            expandedDescriptions: {
                ...previousState.expandedDescriptions,
                comparison: false,
            },
            overflowingDescriptions: {
                ...previousState.overflowingDescriptions,
                comparison: false,
            },
        }), this.refreshTooltipLayout);
    };

    handleViewportChange = () => {
        this.refreshTooltipLayout();
    };

    handleDocumentDragStart = () => {
        this.props.onRequestClose?.();
    };

    handleDocumentPointerDown = (event) => {
        if (!this.props.interactive || this.tooltipRef.current?.contains(event.target)) {
            return;
        }

        const anchorElement = this.props.getAnchorElement?.();
        if (anchorElement?.contains(event.target)) {
            return;
        }

        this.props.onRequestClose?.();
    };

    handleDocumentKeyDown = (event) => {
        if (!this.props.interactive) {
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            this.props.onRequestClose?.();
            return;
        }

        const menuItems = [...(
            this.tooltipRef.current?.querySelectorAll('[role="menuitem"]:not(:disabled)') || []
        )];
        if (menuItems.length === 0) {
            return;
        }
        const currentIndex = menuItems.indexOf(document.activeElement);
        let nextIndex = null;
        if (event.key === 'ArrowDown') {
            nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % menuItems.length;
        } else if (event.key === 'ArrowUp') {
            nextIndex = currentIndex < 0 ? menuItems.length - 1 : (currentIndex - 1 + menuItems.length) % menuItems.length;
        } else if (event.key === 'Home') {
            nextIndex = 0;
        } else if (event.key === 'End') {
            nextIndex = menuItems.length - 1;
        }

        if (nextIndex !== null && menuItems[nextIndex]) {
            event.preventDefault();
            menuItems[nextIndex].focus();
        }
    };

    focusFirstAction = () => {
        const target = this.tooltipRef.current?.querySelector('[role="menuitem"]:not(:disabled)')
            || this.tooltipRef.current?.querySelector('.tooltip-close');
        target?.focus({ preventScroll: true });
    };

    setDescriptionRef = (descriptionKey, node) => {
        if (this.descriptionRefs[descriptionKey] === node) {
            return;
        }

        this.descriptionRefs[descriptionKey] = node;
        if (node) {
            this.refreshTooltipLayout();
        }
    };

    measureDescriptionOverflow = (onMeasured) => {
        if (!this.props.interactive) {
            return false;
        }

        const nextOverflow = { ...this.state.overflowingDescriptions };
        let changed = false;
        Object.entries(this.descriptionRefs).forEach(([descriptionKey, node]) => {
            if (!node || this.state.expandedDescriptions[descriptionKey]) {
                return;
            }

            const isOverflowing = node.scrollHeight > node.clientHeight + 1;
            if (nextOverflow[descriptionKey] !== isOverflowing) {
                nextOverflow[descriptionKey] = isOverflowing;
                changed = true;
            }
        });

        if (changed) {
            this.setState({ overflowingDescriptions: nextOverflow }, onMeasured);
        }
        return changed;
    };

    refreshTooltipLayout = (focusAction = false) => {
        const updatePosition = () => this.updatePosition(focusAction);
        if (!this.measureDescriptionOverflow(updatePosition)) {
            updatePosition();
        }
    };

    toggleDescription = (descriptionKey) => {
        this.setState((previousState) => ({
            expandedDescriptions: {
                ...previousState.expandedDescriptions,
                [descriptionKey]: !previousState.expandedDescriptions[descriptionKey],
            },
        }), this.updatePosition);
    };

    updatePosition = (focusAction = false) => {
        if (!this.tooltipRef.current) {
            return;
        }

        const anchorRect = this.props.getAnchorRect ? this.props.getAnchorRect() : this.props.anchorRect;
        if (!anchorRect) {
            return;
        }

        const tooltipRect = this.tooltipRef.current.getBoundingClientRect();
        const spacing = 12;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let top = anchorRect.top - tooltipRect.height - spacing;
        if (top < spacing) {
            top = anchorRect.bottom + spacing;
        }

        let left = anchorRect.left + (anchorRect.width / 2) - (tooltipRect.width / 2);
        const maxLeft = viewportWidth - tooltipRect.width - spacing;
        const minLeft = spacing;
        left = Math.min(Math.max(left, minLeft), Math.max(minLeft, maxLeft));

        if (top + tooltipRect.height > viewportHeight - spacing) {
            top = Math.max(spacing, viewportHeight - tooltipRect.height - spacing);
        }

        const { position, positioned } = this.state;
        if (Math.round(position.top) !== Math.round(top) || Math.round(position.left) !== Math.round(left) || !positioned) {
            this.setState(
                { position: { top, left }, positioned: true },
                focusAction ? this.focusFirstAction : undefined,
            );
        } else if (focusAction) {
            this.focusFirstAction();
        }
    };

    renderConsumableEffects = (item, descriptionKey) => {
        const effects = getConsumableEffects(item);
        if (effects.length === 0) {
            return null;
        }

        const instantEffects = effects.filter(effect => !isTimedConsumableEffect(effect));
        const timedEffects = effects.filter(isTimedConsumableEffect);
        const duration = formatConsumableDuration(
            getConsumableDurationSeconds(item),
        );
        const titleId = `${this.tooltipInstanceId}-${descriptionKey}-effects-title`;

        return (
            <section
                aria-labelledby={titleId}
                className="tooltip-consumable-effects"
            >
                <div className="tooltip-consumable-effects__title" id={titleId}>
                    On use
                </div>
                {instantEffects.length > 0 && (
                    <ul>
                        {instantEffects.map((effect, index) => (
                            <li key={`instant-${getConsumableEffectType(effect)}-${index}`}>
                                {formatConsumableEffect(
                                    effect,
                                    this.state.consumableEffectTargets,
                                )}
                            </li>
                        ))}
                    </ul>
                )}
                {timedEffects.length > 0 && (
                    <div className="tooltip-consumable-effects__timed">
                        <span>For {duration || 'a configured duration'}:</span>
                        <ul>
                            {timedEffects.map((effect, index) => (
                                <li key={`timed-${getConsumableTargetValue(effect)}-${index}`}>
                                    {formatConsumableEffect(
                                        effect,
                                        this.state.consumableEffectTargets,
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </section>
        );
    };

    renderItemDetails = (
        item,
        rarity,
        itemType,
        contextLabel,
        descriptionKey,
        comparisonStats = null,
        isComparison = false,
    ) => (
        <section
            aria-label={contextLabel ? `${contextLabel}: ${item.name}` : item.name}
            className="tooltip-item-details"
            style={rarity?.colorHex ? { '--pl-item-rarity-color': rarity.colorHex } : undefined}
        >
            {contextLabel && <div className="tooltip-context-label">{contextLabel}</div>}
            <div className="tooltip-header">
                <div className="tooltip-title">{item.name}</div>
                {(rarity?.name || itemType?.name || Number(item.itemLevel) > 0) && (
                    <div className="tooltip-metadata">
                        {rarity?.name && <span className="tooltip-rarity">{rarity.name}</span>}
                        {Number(item.itemLevel) > 0 && (
                            <span className="tooltip-item-level">
                                Item Lv. {Math.max(1, Math.round(Number(item.itemLevel)))}
                            </span>
                        )}
                        {itemType?.name && <span className="tooltip-item-type">{itemType.name}</span>}
                    </div>
                )}
            </div>
            <div className="tooltip-body">
                <ImageLoader
                    className="tooltip-image"
                    imageId={item.imageId}
                    animatedMediaId={item.animatedMediaId || item.animatedImageId}
                    variant={MEDIA_VARIANTS.PICTURE}
                />
                {(item.flavourText || isComparison) && (
                    <div
                        className={`tooltip-description-wrapper ${isComparison ? 'is-comparison' : ''} ${this.state.expandedDescriptions[descriptionKey] ? 'is-expanded' : ''}`.trim()}
                    >
                        {item.flavourText && (
                            <div
                                className={`tooltip-description is-collapsible ${this.state.expandedDescriptions[descriptionKey] ? 'is-expanded' : ''}`.trim()}
                                id={`${this.tooltipInstanceId}-${descriptionKey}-description`}
                                ref={this.descriptionRefCallbacks[descriptionKey]}
                            >
                                {item.flavourText}
                            </div>
                        )}
                        {this.props.interactive && this.state.overflowingDescriptions[descriptionKey] && (
                            <button
                                aria-controls={`${this.tooltipInstanceId}-${descriptionKey}-description`}
                                aria-expanded={this.state.expandedDescriptions[descriptionKey]}
                                className="tooltip-description-toggle"
                                onClick={() => this.toggleDescription(descriptionKey)}
                                type="button"
                            >
                                {this.state.expandedDescriptions[descriptionKey] ? 'Show less' : 'Read more'}
                            </button>
                        )}
                    </div>
                )}
                {this.renderConsumableEffects(item, descriptionKey)}
                <GameItemStats
                    comparisonStats={comparisonStats}
                    stats={item.stats?.statEntries}
                />
            </div>
        </section>
    );

    renderActions = (item, availableActions) => this.props.interactive && (
        <div
            aria-label={`Actions for ${item.name}`}
            className="tooltip-actions"
            role="menu"
        >
            {availableActions.length > 0 ? availableActions.map((action) => (
                <button
                    className="pl-button pl-button--secondary tooltip-action"
                    disabled={action.disabled}
                    key={action.id}
                    onClick={action.onSelect}
                    role="menuitem"
                    title={action.disabledReason || undefined}
                    type="button"
                >
                    {action.label}
                    {action.disabled && action.disabledReason ? (
                        <span className="tooltip-action__reason">{action.disabledReason}</span>
                    ) : null}
                </button>
            )) : (
                <span className="tooltip-actions__empty">No actions available</span>
            )}
        </div>
    );

    render() {
        const {
            item,
            rarity,
            itemType,
            comparisonItem,
            comparisonRarity,
            comparisonItemType,
            position,
            positioned,
        } = this.state;
        if (!item) {
            return null;
        }

        const availableActions = (this.props.actions || []).filter((action) => (
            !action.isAvailable || (itemType && action.isAvailable(item, itemType))
        ));

        const tooltiprender = (
            <div>
                <div
                    className={`tooltip1 ${comparisonItem ? 'tooltip1--comparison' : ''}`.trim()}
                    role={this.props.interactive ? undefined : 'tooltip'}
                    style={rarity?.colorHex ? { '--pl-item-rarity-color': rarity.colorHex } : undefined}
                >
                    <div className="tooltip-inner">
                        <div className="tooltip-items">
                            {comparisonItem && (
                                <div className="tooltip-item-card tooltip-item-card--equipped">
                                    {this.renderItemDetails(
                                        comparisonItem,
                                        comparisonRarity,
                                        comparisonItemType,
                                        this.props.comparisonLabel || 'Equipped',
                                        'comparison',
                                        item.stats?.statEntries,
                                        true,
                                    )}
                                </div>
                            )}
                            <div className="tooltip-item-card tooltip-item-card--pack">
                                {this.renderItemDetails(
                                    item,
                                    rarity,
                                    itemType,
                                    comparisonItem ? 'In Pack' : null,
                                    'primary',
                                    comparisonItem?.stats?.statEntries,
                                    Boolean(comparisonItem),
                                )}
                            </div>
                        </div>
                    </div>
                    {this.renderActions(item, availableActions)}
                </div>
            </div>
        );

        const portalTarget = typeof document !== 'undefined' ? document.body : null;
        if (!portalTarget) {
            return null;
        }

        const content = (
            <div
                ref={this.tooltipRef}
                className={`tooltip-portal ${positioned ? 'is-visible' : ''} ${this.props.interactive ? 'is-interactive' : ''} ${comparisonItem ? 'has-comparison' : ''}`.trim()}
                style={{ top: position.top, left: position.left, visibility: positioned ? 'visible' : 'hidden' }}
            >
                {this.props.interactive && this.props.onRequestClose && (
                    <div className="tooltip-close-layer">
                        <button
                            aria-label="Close item details"
                            className="tooltip-close"
                            onClick={this.props.onRequestClose}
                            type="button"
                        >
                            <span aria-hidden="true">×</span>
                        </button>
                    </div>
                )}
                {tooltiprender}
            </div>
        );

        return createPortal(content, portalTarget);
    }
}
